import {
  customElement,
  html,
  css,
  type TemplateResult,
  DeesElement,
  type CSSResult,
  property,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';

import { DeesInputCheckbox } from '../../00group-input/dees-input-checkbox/dees-input-checkbox.js';
import { DeesInputCode } from '../../00group-input/dees-input-code/dees-input-code.js';
import { DeesInputDatepicker } from '../../00group-input/dees-input-datepicker/index.js';
import { DeesInputText } from '../../00group-input/dees-input-text/dees-input-text.js';
import { DeesInputQuantitySelector } from '../../00group-input/dees-input-quantityselector/dees-input-quantityselector.js';
import { DeesInputRadiogroup } from '../../00group-input/dees-input-radiogroup/dees-input-radiogroup.js';
import { DeesInputDropdown } from '../../00group-input/dees-input-dropdown/dees-input-dropdown.js';
import { DeesInputFileupload } from '../../00group-input/dees-input-fileupload/index.js';
import { DeesInputIban } from '../../00group-input/dees-input-iban/dees-input-iban.js';
import { DeesInputMultitoggle } from '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';
import { DeesInputPhone } from '../../00group-input/dees-input-phone/dees-input-phone.js';
import { DeesInputToggle } from '../../00group-input/dees-input-toggle/dees-input-toggle.js';
import { DeesInputTypelist } from '../../00group-input/dees-input-typelist/dees-input-typelist.js';
import { DeesInputTags } from '../../00group-input/dees-input-tags/dees-input-tags.js';
import { DeesInputList } from '../../00group-input/dees-input-list/dees-input-list.js';
import { DeesInputWysiwyg } from '../../00group-input/dees-input-wysiwyg/dees-input-wysiwyg.js';
import { DeesInputRichtext } from '../../00group-input/dees-input-richtext/component.js';
import { DeesFormSubmit } from '../dees-form-submit/dees-form-submit.js';
import { DeesTable } from '../../00group-dataview/dees-table/index.js';
import { demoFunc } from './dees-form.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

// Unified set for form input types
const FORM_INPUT_TYPES = [
  DeesInputCheckbox,
  DeesInputCode,
  DeesInputDatepicker,
  DeesInputDropdown,
  DeesInputFileupload,
  DeesInputIban,
  DeesInputMultitoggle,
  DeesInputPhone,
  DeesInputQuantitySelector,
  DeesInputRadiogroup,
  DeesInputText,
  DeesInputToggle,
  DeesInputTypelist,
  DeesInputTags,
  DeesInputList,
  DeesInputWysiwyg,
  DeesInputRichtext,
  DeesTable,
];

export interface IFormCompatibleElement extends HTMLElement {
  key?: string;
  label?: string;
  value?: unknown;
  required?: boolean;
  disabled?: boolean;
  layoutMode?: 'vertical' | 'horizontal' | 'auto';
  changeSubject?: {
    subscribe: (callback: (...args: unknown[]) => unknown) => domtools.plugins.smartrx.rxjs.Subscription;
  };
  getValue?: () => unknown;
  setValue?: (value: unknown) => void;
}

export type TFormDataValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | unknown[]
  | File[]
  | { option: string; key: string; payload?: unknown }
  | Record<string, unknown>;

export type TFormData = Record<string, TFormDataValue>;

type TFormElementChangeSubject = {
  subscribe: (callback: () => Promise<void>) => domtools.plugins.smartrx.rxjs.Subscription;
};

export type TFormInputElement =
  | DeesInputCheckbox
  | DeesInputCode
  | DeesInputDatepicker
  | DeesInputDropdown
  | DeesInputFileupload
  | DeesInputIban
  | DeesInputMultitoggle
  | DeesInputPhone
  | DeesInputQuantitySelector
  | DeesInputRadiogroup
  | DeesInputText
  | DeesInputToggle
  | DeesInputTypelist
  | DeesInputTags
  | DeesInputList
  | DeesInputWysiwyg
  | DeesInputRichtext
  | DeesTable<any>
  | IFormCompatibleElement;

function isBuiltInFormInputElement(child: Element): boolean {
  return FORM_INPUT_TYPES.includes(child.constructor as any);
}

function isExternalFormCompatibleElement(child: Element): child is IFormCompatibleElement {
  if (isBuiltInFormInputElement(child)) {
    return false;
  }
  const candidate = child as IFormCompatibleElement;
  return typeof candidate.key === 'string'
    && typeof candidate.changeSubject?.subscribe === 'function'
    && typeof candidate.getValue === 'function'
    && typeof candidate.setValue === 'function';
}

function isFormInputElement(child: Element): child is TFormInputElement {
  return isBuiltInFormInputElement(child) || isExternalFormCompatibleElement(child);
}

function getFormElementValue(child: TFormInputElement): TFormDataValue {
  if (isExternalFormCompatibleElement(child as Element)) {
    return (child as IFormCompatibleElement).getValue!() as TFormDataValue;
  }
  return (child as { value: TFormDataValue }).value;
}

function setFormElementValue(child: TFormInputElement, value: TFormDataValue): void {
  if (isExternalFormCompatibleElement(child as Element)) {
    (child as IFormCompatibleElement).setValue!(value);
    return;
  }
  (child as { value: TFormDataValue }).value = value;
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-form': DeesForm;
  }
}

@customElement('dees-form')
export class DeesForm extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Form'];

  public name: string = 'myform';
  public changeSubject = new domtools.plugins.smartrx.rxjs.Subject();
  public readyDeferred = domtools.plugins.smartpromise.defer();

  /**
   * Controls the layout mode of child input components
   * When true, sets all child inputs to horizontal layout
   */
  @property({ type: Boolean, reflect: true, attribute: 'horizontal-layout' })
  accessor horizontalLayout: boolean = false;

  /**
   * Inset-grouped form: renders inputs as hairline-separated label-left
   * rows inside a single rounded card (settings-list style).
   */
  @property({ type: Boolean, reflect: true })
  accessor grouped: boolean = false;

  public static styles = [
    themeDefaultStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      :host([horizontal-layout]) {
        flex-direction: row;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 16px;
      }

      /* Inset-grouped form: inputs become hairline-separated rows inside a
         single rounded card; rows are label-left with an aligned label column. */
      :host([grouped]) {
        display: block;
        gap: 0;
        background: var(--dees-color-bg-primary);
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: var(--dees-radius-xl);
        overflow: hidden;
        --dees-input-label-width: 38%;
      }

      :host([grouped]) ::slotted(*) {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-height: 44px;
        box-sizing: border-box;
        margin: 0 !important;
        padding: var(--dees-spacing-sm) var(--dees-spacing-lg);
        border-bottom: 1px solid var(--dees-color-border-subtle);
      }

      :host([grouped]) ::slotted(*:last-child) {
        border-bottom: none;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <slot></slot>
    `;
  }

  public async firstUpdated() {
    const formChildren = this.getFormElements();
    this.updateRequiredStatus();
    this.updateChildrenLayoutMode();
    this.applyGroupedMode();

    for (const child of formChildren) {
      const childSubscription = (child.changeSubject as TFormElementChangeSubject).subscribe(async () => {
        const valueObject = await this.collectFormData();
        this.changeSubject.next(valueObject);
        this.updateRequiredStatus();
      });
      this.rxSubscriptions.push(childSubscription);
    }
    await this.addBehaviours();
    this.readyDeferred.resolve();
  }

  public getFormElements(): Array<TFormInputElement> {
    // Use querySelectorAll('*') to find form inputs nested inside wrapper elements (e.g. <div>)
    return Array.from(this.querySelectorAll('*')).filter((child) =>
      isFormInputElement(child)
    ) as unknown as TFormInputElement[];
  }

  public getSubmitButton(): DeesFormSubmit | undefined {
    return Array.from(this.querySelectorAll('*')).find(
      (child) => child instanceof DeesFormSubmit
    ) as DeesFormSubmit;
  }

  public async updateRequiredStatus() {
    let requiredOK = true;
    for (const childArg of this.getFormElements()) {
      if (childArg.required && !getFormElementValue(childArg)) {
        requiredOK = false;
      }
    }
    const submitButton = this.getSubmitButton();
    if (submitButton) {
      submitButton.disabled = !requiredOK;
    }
  }

  /**
   * collects the form data
   * @returns
   */
  public async collectFormData() {
    const children = this.getFormElements();
    const valueObject: TFormData = {};
    
    for (const child of children) {
      if (!child.key) {
        console.log(`form element with label "${child.label}" has no key. skipping.`);
        continue;
      }
      
      valueObject[child.key] = getFormElementValue(child);
    }
    
    return valueObject;
  }

  public async gatherAndDispatch() {
    const valueObject = await this.collectFormData();
    const formDataEvent = new CustomEvent('formData', {
      detail: {
        data: valueObject,
      },
      bubbles: true,
    });
    this.dispatchEvent(formDataEvent);
  }

  public setStatus(
    visualStateArg: 'normal' | 'pending' | 'error' | 'success',
    textStateArg: string
  ) {
    const inputChildren = this.getFormElements();
    const submitButton = this.getSubmitButton();
    if (!submitButton) return;

    switch (visualStateArg) {
      case 'normal':
        submitButton.disabled = false;
        submitButton.status = 'normal';
        for (const inputChild of inputChildren) {
          inputChild.disabled = false;
        }
        break;
      case 'pending':
        submitButton.disabled = true;
        submitButton.status = 'pending';
        for (const inputChild of inputChildren) {
          inputChild.disabled = true;
        }
        break;
      case 'success':
        submitButton.disabled = true;
        submitButton.status = 'success';
        for (const inputChild of inputChildren) {
          inputChild.disabled = true;
        }
        break;
      case 'error':
        submitButton.disabled = true;
        submitButton.status = 'error';
        for (const inputChild of inputChildren) {
          inputChild.disabled = true;
        }
        break;
    }

    submitButton.text = textStateArg;
  }

  /**
   * resets the form
   */
  reset() {
    const inputChildren = this.getFormElements();

    for (const inputChild of inputChildren) {
      setFormElementValue(inputChild, null);
    }
    this.setStatus('normal', 'Submit');
  }

  public async addBehaviours() {
    // Use event delegation
    this.addEventListener('keydown', (event: KeyboardEvent) => {
      const target = event.target as DeesElement;
      if (!isFormInputElement(target)) return;

      if (event.key === 'Enter') {
        const children = this.getFormElements();
        const currentIndex = children.indexOf(target as any);
        if (currentIndex < children.length - 1) {
          children[currentIndex + 1].focus();
        } else {
          target.blur();
          this.getSubmitButton()?.focus();
        }
      }
    });
  }

  /**
   * Updates the layout mode of child input components based on form's horizontalLayout property
   */
  private updateChildrenLayoutMode() {
    const formChildren = this.getFormElements();
    for (const child of formChildren) {
      if ('layoutMode' in child) {
        // The child's auto mode will detect this form's horizontal-layout attribute
        (child as any).layoutMode = 'auto';
      }
    }
  }

  /**
   * In grouped mode, render each input as a label-left row (settings-list style).
   */
  private applyGroupedMode() {
    if (!this.grouped) return;
    for (const child of this.getFormElements()) {
      if ('labelPosition' in child) {
        (child as any).labelPosition = 'left';
      }
    }
  }

  /**
   * Called when properties change
   */
  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    
    if (changedProperties.has('horizontalLayout')) {
      this.updateChildrenLayoutMode();
    }
  }
}
